import { useEffect, useRef, useState } from "react";
import { IconCheck, IconLoader2, IconRefresh, IconUpload } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  useCalibrationHistory,
  useLiveCalibration,
  useWriteCalibrationToBoat,
} from "@/api/hooks/use-calibrations";
import { toParamMap } from "@/domain/calibration-params";
import type { NvpParameter, ParamGroup } from "@/domain/calibration-params";
import {
  boatHolds,
  goldenColumnLabel,
  newerGoldenThan,
  writeOutcome,
} from "@/domain/calibration-live";
import { timeAgo } from "@/lib/time";
import type { BuildId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type Calibration = components["schemas"]["CalibrationResponse"];

/** An armed write disarms itself after this long, like the sync card. */
const CONFIRM_WINDOW_MS = 3000;

/** A golden whose values may be written to the boat. */
export interface WriteSource {
  id: string;
  label: string;
  /** Names a newer golden elsewhere, shown on the confirm so an older
   * event's page cannot overwrite the boat unannounced. */
  warning?: string;
}

/** Everything a params table needs to offer "write this group to the boat". */
export interface WriteControls {
  /** What the boat holds, once read. */
  live?: Map<string, NvpParameter>;
  busy: boolean;
  write: (source: WriteSource, group: ParamGroup) => void;
  /** This build's golden as a source, when it has one. */
  golden?: WriteSource;
}

interface ControlsArgs {
  boatCode?: string | null;
  boatOnline: boolean;
  canWrite: boolean;
  currentGolden?: Calibration;
  eventName?: string;
}

/** Reading the boat and writing to it need the boat online and the write
 * permission, so the live column and the buttons appear together. */
export function useCalibrationWriteControls(
  buildId: BuildId,
  { boatCode, boatOnline, canWrite, currentGolden, eventName }: ControlsArgs,
) {
  const enabled = canWrite && boatOnline && !!boatCode;
  const liveQuery = useLiveCalibration(buildId, enabled);
  const { data: history } = useCalibrationHistory(buildId);
  const write = useWriteCalibrationToBoat(buildId);

  // Writing this build's golden from an older event's page is allowed, but
  // the confirm names the newer golden the boat would otherwise carry.
  const newer =
    currentGolden && history ? newerGoldenThan(currentGolden.fetched_at, history) : null;
  const golden: WriteSource | undefined = currentGolden
    ? {
        id: currentGolden.id,
        label: goldenColumnLabel(eventName),
        warning: newer ? `newer golden at ${newer.event_name}` : undefined,
      }
    : undefined;

  const controls: WriteControls | undefined = enabled
    ? {
        live: liveQuery.data ? toParamMap(liveQuery.data.parameters) : undefined,
        busy: write.isPending,
        golden,
        write: (source, group) =>
          write.mutate(
            { calibrationId: source.id, params: [...group.params] },
            {
              onSuccess: (result) => {
                const outcome = writeOutcome(result);
                if (outcome.ok) toast.success(outcome.message);
                else toast.warning(outcome.message);
              },
              onError: (err) => toast.error(err.message),
            },
          ),
      }
    : undefined;

  return { controls, liveQuery, showLiveStatus: canWrite && !!boatCode };
}

export function LiveStatus({
  boatOnline,
  query,
}: {
  boatOnline: boolean;
  query: ReturnType<typeof useLiveCalibration>;
}) {
  if (!boatOnline) {
    return <span className="text-xs text-muted-foreground/60">Boat offline, no live values</span>;
  }
  if (query.isFetching) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <IconLoader2 className="h-3 w-3 animate-spin" />
        Reading boat…
      </span>
    );
  }
  if (query.isError) {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-destructive">
        {query.error.message}
        <button type="button" onClick={() => query.refetch()} className="underline">
          Retry
        </button>
      </span>
    );
  }
  if (!query.data) return null;

  const unread = query.data.parameters.filter((p) => p.Value == null).length;
  return (
    <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
      <span>
        Live read {timeAgo(query.data.read_at)}
        {unread > 0 && `, ${unread} unread`}
      </span>
      <button
        type="button"
        onClick={() => query.refetch()}
        title="Read the boat again"
        className="text-muted-foreground/60 hover:text-foreground"
      >
        <IconRefresh className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function GroupWriteButton({
  title,
  held,
  busy,
  warning,
  onConfirm,
}: {
  title: string;
  held: boolean;
  busy: boolean;
  warning?: string;
  onConfirm: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (held) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400">
        <IconCheck className="h-3 w-3" />
        on boat
      </span>
    );
  }

  if (armed) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setArmed(false);
          clearTimeout(timer.current);
          onConfirm();
        }}
        className="inline-flex items-center gap-1 rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-900/40"
      >
        Confirm?
        {warning && <span className="font-normal opacity-80">{warning}</span>}
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={busy}
      title={title}
      onClick={() => {
        setArmed(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setArmed(false), CONFIRM_WINDOW_MS);
      }}
      className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400 dark:hover:bg-amber-900/40"
    >
      <IconUpload className="h-3 w-3" />
      Write to boat
    </button>
  );
}

/** One column's write control for a group, or nothing when it has no source. */
export function groupAction(
  controls: WriteControls,
  group: ParamGroup,
  source: WriteSource | undefined,
  stored: Map<string, NvpParameter>,
) {
  if (!source) return undefined;
  const held = controls.live ? boatHolds(group.params, stored, controls.live) : false;
  // Keyed by source and group: a source that changes under an armed button
  // (a golden promoted elsewhere) remounts it disarmed.
  return (
    <GroupWriteButton
      key={`${source.id}:${group.label}`}
      title={`Write the ${group.label} values of ${source.label} to the boat`}
      held={held}
      busy={controls.busy}
      warning={source.warning}
      onConfirm={() => controls.write(source, group)}
    />
  );
}
